
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import arrow from "../../../src/Assets/arrow.png";
import "./Header.scss";
import API from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import { resetSocketConnection } from "../../Api/socket";
import { logout } from "../../Features/Postes/Slice/authSlice";
import PatientNotificationBell from "../../components/PatientNotificationBell/PatientNotificationBell";
import { showConfirm } from "../../feedback/feedbackApi";

function PatientProfileMenu({ patient, variant, onCloseMobileNav }) {
  const [open, setOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const wrapRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [patient?.avatar]);

  const handleLogout = async () => {
    const confirmed = await showConfirm("Are you sure you want to log out?", {
      title: "Log out?",
      confirmText: "Log out",
      cancelText: "Stay",
      danger: true,
    });
    if (!confirmed) return;
    setOpen(false);
    onCloseMobileNav?.();
    dispatch(logout());
    resetSocketConnection();
    navigate("/", { replace: true });
  };

  const goPanel = () => {
    setOpen(false);
    onCloseMobileNav?.();
    navigate("/HealBidDashboard");
  };

  return (
    <div
      className={`header_patient_wrap header_patient_wrap--${variant}`}
      ref={wrapRef}
    >
      <button
        type="button"
        className="header_patient_chip"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title={patient?.name || "Account"}
      >
        {patient?.avatar && !avatarFailed ? (
          <img
            src={patient.avatar}
            alt=""
            className="header_patient_avatar"
            referrerPolicy="no-referrer"
            onError={() => setAvatarFailed(true)}
          />
        ) : (
          <span className="header_patient_avatar header_patient_avatar--placeholder" aria-hidden>
            {(patient?.name || "P").charAt(0).toUpperCase()}
          </span>
        )}
        <span className="header_patient_name">{patient?.name || "…"}</span>
      </button>
      {open && (
        <div className="header_patient_dropdown" role="menu">
          <button type="button" className="header_patient_panel" role="menuitem" onClick={goPanel}>
            My panel
          </button>
          <button type="button" className="header_patient_logout" role="menuitem" onClick={handleLogout}>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const logo = "/Assets/logo_white.png";
  const [isOpen, setIsOpen] = useState(false);
  const [patient, setPatient] = useState(null);
  const location = useLocation();
  const authToken = useSelector((s) => s.auth?.token);
  const profileSuccess = useSelector((s) => s.auth?.profileSuccess);

  const loadPatient = useCallback(async () => {
    const token = tokenManager.getTokenForRole("patient");
    if (!token) {
      setPatient(null);
      return;
    }
    try {
      const res = await API.get(ENDPOINTS.GET_ME, {
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      const body = res?.data ?? res;
      const user = body?.user ?? body?.data?.user ?? null;
      if (!user) {
        setPatient(null);
        return;
      }
      const name = (user.name || "").trim() || "Patient";
      const avatar =
        user.avatar ||
        user.profileImageUrl ||
        body?.avatar ||
        body?.profileImageUrl ||
        "";
      setPatient({ name, avatar });
    } catch {
      setPatient(null);
    }
  }, []);

  useEffect(() => {
    loadPatient();
  }, [loadPatient, authToken, profileSuccess, location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const isLoggedIn = Boolean(tokenManager.getTokenForRole("patient"));

  const authBlock = !isLoggedIn ? (
    <div className="care_flex">
      <Link to="/get-started" onClick={() => setIsOpen(false)}>
        Login / Sign up
      </Link>
      <span className="sp_an">
        <img src={arrow} alt="" />
      </span>
    </div>
  ) : (
    <div className="header_patient_actions">
      <Link to="/HealBidDashboard" className="header_my_requests_link" onClick={() => setIsOpen(false)}>
        My panel
      </Link>
      <PatientNotificationBell variant="header" />
      <PatientProfileMenu
        patient={patient}
        variant="desktop"
        onCloseMobileNav={() => setIsOpen(false)}
      />
    </div>
  );

  const authBlockMobile = !isLoggedIn ? (
    <div className="care_flex">
      <Link to="/get-started" onClick={() => setIsOpen(false)}>
        Login / Sign up
      </Link>
      <span className="sp_an">
        <img src={arrow} alt="" />
      </span>
    </div>
  ) : (
    <div className="header_patient_actions header_patient_actions--mobile">
      <PatientNotificationBell variant="header" />
      <PatientProfileMenu patient={patient} variant="mobile" onCloseMobileNav={() => setIsOpen(false)} />
    </div>
  );

  return (
    <div className={`hov_nav${isOpen ? " hov_nav--menu-open" : ""}`}>
      <div className="navbar">
        <div className="container">
          <div className="all_nav_flex">
            <div className="lef_t">
              <img src={logo} alt="logo" />
            </div>

            <button
              type="button"
              className="menu_toggle"
              onClick={toggleMenu}
              aria-expanded={isOpen}
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? "✖" : "☰"}
            </button>

            {isOpen && (
              <button
                type="button"
                className="mobile_nav_scrim"
                aria-label="Close menu"
                onClick={() => setIsOpen(false)}
              />
            )}

            <div className={`mi_d ${isOpen ? "active" : ""}`}>
              <div className="mi_d_topbar">
                <Link
                  to="/"
                  className="mi_d_brand"
                  onClick={() => setIsOpen(false)}
                  aria-label="HealBid home"
                >
                  <img src={logo} alt="" />
                </Link>
                <button
                  type="button"
                  className="mi_d_close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close menu"
                >
                  ✖
                </button>
              </div>
              <ul className="ankertag">
                <li>
                  <Link to="/" onClick={() => setIsOpen(false)}>
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/hospital-login" onClick={() => setIsOpen(false)}>
                    Hospitals
                  </Link>
                </li>
                <li>
                  <Link to="/DoctorSignup" onClick={() => setIsOpen(false)}>
                    Doctors
                  </Link>
                </li>
                <li>
                  <Link to="/AboutUs" onClick={() => setIsOpen(false)}>
                    About us
                  </Link>
                </li>
                {!isLoggedIn ? (
                  <li>
                    <Link to="/get-started" onClick={() => setIsOpen(false)}>
                      Patient Login / Sign up
                    </Link>
                  </li>
                ) : null}
                {isLoggedIn ? (
                  <>
                    <li>
                      <Link to="/care-price" onClick={() => setIsOpen(false)}>
                        Care &amp; pricing
                      </Link>
                    </li>
                    <li>
                      <Link to="/patient-chats" onClick={() => setIsOpen(false)}>
                        Messages
                      </Link>
                    </li>
                    <li>
                      <Link to="/HealBidDashboard" onClick={() => setIsOpen(false)}>
                        My panel
                      </Link>
                    </li>
                  </>
                ) : null}
              </ul>
              <div className="header_auth header_auth--mobile">{authBlockMobile}</div>
            </div>

            <div className="ri_ght">
              <div className="header_auth header_auth--desktop">{authBlock}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
